import "../../../assets/css/PremiumTools.css";
import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Zap,
  RotateCcw,
  FileText,
  Copy,
  Video,
  Sliders
} from "lucide-react";

import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import auth from "../../../firebase.init";

// Configuration
const API_ROOT = process.env.REACT_APP_API_URL || "https://allinone-clone-7d5d795e98ce.herokuapp.com";
const MAX_WORDS = 800;

// --- Options Constants ---
const PLATFORM_OPTIONS = [
  "YouTube Video", "YouTube Short", "Instagram Reel", "TikTok",
  "Facebook Video", "Video Ad", "Explainer Video",
];

const STYLE_OPTIONS = [
  "Educational / How-to", "Storytelling / Narrative", "Promotional / Sales",
  "Conversational / Vlog", "Professional / Corporate", "Funny / Entertaining",
];

// NEW: Word Limit Options replacing Duration
const WORD_LIMIT_OPTIONS = [
  "150 Words (Shorts/Reels)",
  "300 Words (1 Min)",
  "500 Words (3-5 Mins)",
  "800 Words (Long Form)",
  "1000 Words (In Depth)",
];

const VOICE_OPTIONS = [
  { label: "Achernar — Warm", value: "achernar" },
  { label: "Achird — Calm", value: "achird" },
  { label: "Algenib — Deep", value: "algenib" },
  { label: "Algieba — Smooth", value: "algieba" },
  { label: "Alnilam — Clear", value: "alnilam" },
  { label: "Aoede — Light", value: "aoede" },
  { label: "Autonoe — Soft", value: "autonoe" },
  { label: "Callirrhoe — Balanced", value: "callirrhoe" },
  { label: "Charon — Informative", value: "charon" },
  { label: "Despina — Strong", value: "despina" },
  { label: "Enceladus — Neutral", value: "enceladus" },
  { label: "Erinome — Gentle", value: "erinome" },
  { label: "Fenrir — Excitable", value: "fenrir" },
  { label: "Gacrux — Bright", value: "gacrux" },
  { label: "Iapetus — Natural", value: "iapetus" },
  { label: "Kore — Firm", value: "kore" },
  { label: "Laomedeia — Soft", value: "laomedeia" },
];

// --- Helper Functions ---
const pcmToWav = (pcmData, sampleRate, numChannels = 1, bitsPerSample = 16) => {
  const byteRate = (sampleRate * numChannels * bitsPerSample) / 8;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const buffer = new ArrayBuffer(44 + pcmData.byteLength);
  const view = new DataView(buffer);

  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  let offset = 0;
  writeString(view, offset, "RIFF");
  offset += 4;
  view.setUint32(offset, 36 + pcmData.byteLength, true);
  offset += 4;
  writeString(view, offset, "WAVE");
  offset += 4;
  writeString(view, offset, "fmt ");
  offset += 4;
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, byteRate, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bitsPerSample, true);
  offset += 2;
  writeString(view, offset, "data");
  offset += 4;
  view.setUint32(offset, pcmData.byteLength, true);
  offset += 4;

  for (let i = 0; i < pcmData.byteLength; i++) {
    view.setUint8(offset + i, pcmData[i]);
  }

  return new Blob([buffer], { type: "audio/wav" });
};

const VideoScriptGenerator = () => {
  const navigate = useNavigate();
  const [user, loadingAuth] = useAuthState(auth);

  // Loading States
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatingVoice, setGeneratingVoice] = useState(false);

  // Data States
  const [apiKeys, setApiKeys] = useState([]);
  const [credits, setCredits] = useState(0);

  // Form State
  const [scriptParams, setScriptParams] = useState({
    topic: "",
    platform: "YouTube Video",
    style: "Educational / How-to",
    wordLimit: "500 Words (3-5 Mins)", // Replaced duration with wordLimit
    audience: "",
    tone: "",
    cta: "",
  });

  const [generatedScript, setGeneratedScript] = useState("");
  const [generatedAudioMeta, setGeneratedAudioMeta] = useState(null);
  const [speaker1Voice, setSpeaker1Voice] = useState("achernar");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // --- API & Auth Init ---
  const fetchApiKeys = async () => {
    try {
      const resp = await axios.get(`${API_ROOT}/api/gemini-keys`);
      if (resp.data?.success && Array.isArray(resp.data.data)) {
        const keys = resp.data.data.map((k) => k.key).filter(Boolean);
        setApiKeys(keys);
        if (!keys.length) setError("No API keys available. Contact admin.");
      } else setError("Failed to load API keys");
    } catch (err) {
      console.error("fetchApiKeys err:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  const getRandomApiKey = () => {
    if (!apiKeys.length) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  useEffect(() => {
    const init = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      setError("");
      setSuccess("");
      try {
        await fetchApiKeys();
        const uResp = await axios.get(`${API_ROOT}/users`, {
          params: { userEmail: user.email },
        });
        if (uResp.data?.length > 0)
          setCredits(uResp.data[0].currentBalance ?? 0);
        else setError("User data not found");
      } catch (err) {
        console.error("init err:", err);
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, loadingAuth]);

  // --- Credit Logic ---
  const deductCredits = async (amount) => {
    const resp = await axios.post(`${API_ROOT}/api/deduct-credit`, {
      userEmail: user.email,
      amount,
    });
    if (!resp.data?.success) throw new Error("Failed to deduct credits");
    setCredits((p) => p - amount);
    return true;
  };

  const refundCredits = async (amount) => {
    try {
      const resp = await axios.post(`${API_ROOT}/api/refund-credit`, {
        userEmail: user.email,
        amount,
      });
      if (resp.data?.success) setCredits((p) => p + amount);
    } catch (err) {
      console.error("refundCredits err:", err);
    }
  };

  // --- Input Handler ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setScriptParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --- Generation Logic ---
  const generateScript = async () => {
    setError("");
    setSuccess("");

    if (!user) return setError("Please login to generate scripts");
    if (!scriptParams.topic.trim()) return setError("Video Topic is required");
    if (credits < 1) return setError("Insufficient credits. Need 1 credit");
    if (!apiKeys.length) return setError("No API keys available");

    setGeneratingScript(true);
    setGeneratedScript("");
    setGeneratedAudioMeta(null);

    try {
      await deductCredits(1);
    } catch (err) {
      setGeneratingScript(false);
      return setError("Failed to deduct credit: " + err.message);
    }

    try {
      const MODEL_ID = "gemma-4-31b-it";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const fullPrompt = `
        Act as a professional video scriptwriter. Create a script based on these details:
        
        - Platform: ${scriptParams.platform}
        - Topic: "${scriptParams.topic}"
        - Style: ${scriptParams.style}
        - Target Audience: ${scriptParams.audience || "General"}
        - Word Limit: ${scriptParams.wordLimit} (Strictly adhere to this word count)
        - Tone of Voice: ${scriptParams.tone || "Engaging"}
        - Call to Action (CTA): ${scriptParams.cta || "Subscribe/Follow"}

        Required Structure:
        1. **Hook**: Grab attention immediately.
        2. **Intro**: Briefly introduce the topic.
        3. **Main Content**: The core message/value.
        4. **CTA / Outro**: Clear direction for the viewer.

        Format requirements:
        - Output ONLY the script text (and speaker directions in parentheses if needed).
        - Make it sound natural and spoken, not robotic.
        - Do not include introductory conversational filler.
      `;

      const requestBody = {
        contents: [{ role: "user", parts: [{ text: fullPrompt }] }],
        generationConfig: { maxOutputTokens: 1500 },
      };

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API Error: ${res.status} - ${text}`);
      }

      const data = await res.json();
      let scriptText = data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!scriptText) throw new Error("No script returned from API");

      scriptText = scriptText.replace(/```/g, "").trim();
      const words = scriptText.trim().split(/\s+/);
      if (words.length > MAX_WORDS) {
        scriptText = words.slice(0, MAX_WORDS).join(" ");
      }

      setGeneratedScript(scriptText);
      setSuccess("Script generated successfully!");

    } catch (err) {
      console.error(err);
      await refundCredits(1);
      setError("Script generation failed: " + (err.message || err));
    } finally {
      setGeneratingScript(false);
    }
  };

  const generateVoiceFromScript = async () => {
    setError("");
    setSuccess("");
    if (!user) return setError("Please login to generate voice");

    const scriptToUse = generatedScript || scriptParams.topic;
    if (!scriptToUse) return setError("Generate a script first.");

    const creditsNeeded = 1;
    if (credits < creditsNeeded)
      return setError(`Insufficient credits. Need ${creditsNeeded} credit(s)`);

    setGeneratingVoice(true);
    let creditsDeducted = 0;

    try {
      await deductCredits(creditsNeeded);
      creditsDeducted = creditsNeeded;
    } catch (err) {
      setGeneratingVoice(false);
      return setError("Failed to deduct credit: " + err.message);
    }

    try {
      const apiKey = getRandomApiKey();
      const ttsApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-tts:generateContent?key=${apiKey}`;

      const cleanScript = scriptToUse.replace(/[*#]/g, "");

      const payload = {
        contents: [
          {
            role: "user",
            parts: [{ text: cleanScript }],
          },
        ],
        generationConfig: {
          responseModalities: ["audio"],
          speech_config: {
            voice_config: {
              prebuilt_voice_config: { voice_name: speaker1Voice },
            },
          },
        },
      };

      const response = await fetch(ttsApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Gemini TTS API Error: ${response.status} - ${text}`);
      }

      const data = await response.json();
      const audioPart = data?.candidates?.[0]?.content?.parts?.[0];
      if (!audioPart?.inlineData?.data)
        throw new Error("No audio returned from TTS API");

      const base64Audio = audioPart.inlineData.data;
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const wavBlob = pcmToWav(bytes, 24000);
      const blobUrl = URL.createObjectURL(wavBlob);

      setGeneratedAudioMeta({
        blob: wavBlob,
        blobUrl,
        filename: `script-audio-${Date.now()}.wav`,
        speakers: [speaker1Voice],
      });

      setSuccess("Voice generated successfully!");
    } catch (err) {
      console.error(err);
      await refundCredits(creditsDeducted);
      setError("Voice generation failed: " + (err.message || err));
    } finally {
      setGeneratingVoice(false);
    }
  };

  const downloadAudio = () => {
    if (!generatedAudioMeta) return;
    const link = document.createElement("a");
    link.href = generatedAudioMeta.blobUrl;
    link.download = generatedAudioMeta.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(generatedScript);
    setSuccess("Script copied to clipboard!");
    setTimeout(() => setSuccess(""), 3000);
  };

  const resetAll = () => {
    setScriptParams({
      topic: "",
      platform: "YouTube Video",
      style: "Educational / How-to",
      wordLimit: "500 Words (3-5 Mins)", // Reset to default
      audience: "",
      tone: "",
      cta: "",
    });
    setGeneratedScript("");
    setGeneratedAudioMeta(null);
    setSpeaker1Voice("achernar");
    setError("");
    setSuccess("");
  };

  if (loading || loadingKeys)
    return (
      <div className="d-flex justify-content-center align-items-center bg-dark" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      <div className="container py-5 position-relative z-1">
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Video Script <span className="text-neon">Generator</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 3 && (
              <button className="btn-buy-credits" onClick={() => navigate("/user/select-package")}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}
        {success && <div className="alert alert-success glass-alert bg-success bg-opacity-10 text-success border-success">{success}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-5">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Sliders size={18} className="text-neon" />
                  Script Details
                </h5>
              </div>

              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Platform</label>
                    <select 
                      className="form-select input-group-glass"
                      name="platform"
                      value={scriptParams.platform}
                      onChange={handleInputChange}
                    >
                      {PLATFORM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Video Style</label>
                    <select 
                      className="form-select input-group-glass"
                      name="style"
                      value={scriptParams.style}
                      onChange={handleInputChange}
                    >
                      {STYLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Video Topic / Title *</label>
                  <input 
                    type="text"
                    className="form-control input-group-glass py-2"
                    placeholder="e.g., How to bake sourdough bread for beginners"
                    name="topic"
                    value={scriptParams.topic}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Word Limit</label>
                    <select 
                      className="form-select input-group-glass"
                      name="wordLimit"
                      value={scriptParams.wordLimit}
                      onChange={handleInputChange}
                    >
                      {WORD_LIMIT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Tone</label>
                    <input 
                      type="text"
                      className="form-control input-group-glass"
                      placeholder="e.g. Enthusiastic"
                      name="tone"
                      value={scriptParams.tone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Target Audience</label>
                    <input 
                      type="text"
                      className="form-control input-group-glass"
                      placeholder="e.g. Home cooks"
                      name="audience"
                      value={scriptParams.audience}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-light opacity-75">Call to Action</label>
                    <input 
                      type="text"
                      className="form-control input-group-glass"
                      placeholder="e.g. Subscribe"
                      name="cta"
                      value={scriptParams.cta}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="mt-auto pt-3 d-flex gap-2">
                  <button 
                    className="btn btn-neon-primary w-100 d-flex align-items-center justify-content-center gap-2" 
                    onClick={generateScript}
                    disabled={generatingScript || generatingVoice || !scriptParams.topic.trim() || credits < 1}
                  >
                    {generatingScript ? (
                      <>
                        <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                        Writing...
                      </>
                    ) : (
                      <>
                        <Zap size={18} /> Generate Script (1 Credit)
                      </>
                    )}
                  </button>
                  <button className="btn btn-outline-light px-3" onClick={resetAll} disabled={generatingScript} title="Reset">
                    <RotateCcw size={18} />
                  </button>
                </div>

              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUTS */}
          <div className="col-lg-7">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <FileText size={18} className={generatedScript ? "text-success" : "text-neon"} />
                  Generated Script
                </h5>
                {generatedScript && !generatingScript && (
                  <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2" onClick={handleCopyScript}>
                    <Copy size={14} /> Copy Script
                  </button>
                )}
              </div>

              <div className="card-body-glass flex-grow-1 overflow-auto position-relative p-0" style={{ minHeight: "500px" }}>
                {generatingScript ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-white animate-pulse">AI is crafting your story...</h5>
                  </div>
                ) : generatedScript ? (
                  <textarea
                    className="form-control bg-transparent text-white border-0 h-100 p-4"
                    style={{ resize: "none", minHeight: "500px", fontSize: "1.05rem", lineHeight: "1.7", outline: "none", boxShadow: "none" }}
                    value={generatedScript}
                    readOnly
                  ></textarea>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <Video size={64} className="mb-3" />
                    <h3 className="fs-4 mb-2 text-white opacity-50">No Script Generated Yet</h3>
                    <p className="text-center px-4">Fill out the details on the left to start generating your video script.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoScriptGenerator;
